import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { RecentSearch, RecentSearchesService } from '../services/recent-searches.service';
import { SearchStateService } from '../services/search-state.service';

@Component({
  selector: 'app-recent-searches',
  templateUrl: './recent-searches.component.html',
  styleUrls: ['./recent-searches.component.scss'],
  standalone: false
})
export class RecentSearchesComponent implements OnInit {
  private recentSearchesService = inject(RecentSearchesService);
  private searchStateService = inject(SearchStateService);
  private router = inject(Router);

  recentSearches: RecentSearch[] = [];

  ngOnInit(): void {
    this.loadRecentSearches();
  }

  loadRecentSearches(): void {
    this.recentSearches = this.recentSearchesService.getRecentSearches();
  }

  onSearchClick(search: RecentSearch): void {
    // Determine the tab and search parameters based on search type
    let tab = 'patients';
    let queryParams: any = {};

    switch (search.type) {
      case 'name':
        // Patient Search - Name & DOB tab
        tab = 'patients';
        queryParams = {
          rerun: 'true',
          type: 'name',
          query: search.query,
          firstName: search.firstName || '',
          lastName: search.lastName || '',
          dob: search.dob || ''
        };
        break;

      case 'mpi':
        // Patient Search - MPI tab
        tab = 'patients';
        queryParams = {
          rerun: 'true',
          type: 'mpi',
          query: search.query
        };
        break;

      case 'identifier':
        // Patient Search - Identifier tab
        tab = 'patients';
        queryParams = {
          rerun: 'true',
          type: 'identifier',
          query: search.query,
          identifierType: search.identifierType || 'euid'
        };
        break;

      case 'facilityNumber':
        // Patient Search - Facility tab
        tab = 'patients';
        queryParams = {
          rerun: 'true',
          type: 'facilityNumber',
          query: search.query,
          excludeInactivePatients: search.excludeInactivePatients || ''
        };
        break;

      case 'teammate':
        // Teammate Search
        tab = 'teammates';
        queryParams = {
          rerun: 'true',
          type: 'teammate',
          query: search.query
        };
        break;

      case 'teammate_username':
        // Teammate Search
        tab = 'teammates';
        queryParams = {
          rerun: 'true',
          type: 'teammate_username',
          query: search.query,
          dataSource: search.selectedDataSource || 'oim_cwow'
        };
        break;

      case 'teammate_facility':
        // Teammate Search
        tab = 'teammates';
        queryParams = {
          rerun: 'true',
          type: 'teammate_facility',
          query: search.query
        };
        break;

      case 'provider':
        // Provider Search
        tab = 'providers';
        queryParams = {
          rerun: 'true',
          type: 'provider',
          query: search.query
        };
        break;

      case 'careteamlookup':
        // Care Team Lookup
        tab = 'careteamlookup';
        queryParams = {
          rerun: 'true',
          type: 'careteamlookup',
          query: search.query,
          facilityNumber: search.facilityNumber || ''
        };
        break;

      case 'appointment_mpi':
        // Appointment Lookup - MPI
        tab = 'appointments';
        queryParams = {
          rerun: 'true',
          type: 'appointment_mpi',
          query: search.query,
          startDate: search.startDate || '',
          endDate: search.endDate || ''
        };
        break;

      case 'appointment_facility':
        // Appointment Lookup - Facility
        tab = 'appointments';
        queryParams = {
          rerun: 'true',
          type: 'appointment_facility',
          query: search.query,
          startDate: search.startDate || '',
          endDate: search.endDate || ''
        };
        break;

      case 'appointment_eventId':
        // Appointment Lookup - Event ID
        tab = 'appointments';
        queryParams = {
          rerun: 'true',
          type: 'appointment_eventId',
          query: search.query,
          startDate: search.startDate || '',
          endDate: search.endDate || ''
        };
        break;

      case 'appointment_timerange':
        // Appointment Lookup - Time Range
        tab = 'appointments';
        queryParams = {
          rerun: 'true',
          type: 'appointment_timerange',
          query: search.timeRangeStartDateTime || search.query,
          timeRangeStartDateTime: search.timeRangeStartDateTime || '',
          timeRangeEndDateTime: search.timeRangeEndDateTime || ''
        };
        break;

      default:
        // Fallback to patients tab
        tab = 'patients';
        queryParams = {
          rerun: 'true',
          type: search.type,
          query: search.query
        };
    }

    // Add tab parameter
    queryParams.tab = tab;

    // Set up the search state
    const searchData = {
      query: search.query,
      type: search.type,
      firstName: search.firstName,
      lastName: search.lastName,
      dob: search.dob,
      identifierType: search.identifierType,
      excludeInactivePatients: search.excludeInactivePatients,
      facilityNumber: search.facilityNumber
    };

    this.searchStateService.setLastSearchQuery(searchData);
    this.router.navigate(['/search'], { queryParams });
  }

  onRemoveSearch(event: Event, search: RecentSearch): void {
    event.stopPropagation();
    this.recentSearchesService.removeSearch(search.id);
    this.loadRecentSearches();
  }

  onClearAll(): void {
    this.recentSearchesService.clearAll();
    this.loadRecentSearches();
  }

  formatSearchDisplay(search: RecentSearch): string {
    return this.recentSearchesService.formatSearchDisplay(search);
  }

  getSearchTypeLabel(type: string): string {
    return this.recentSearchesService.getSearchTypeLabel(type);
  }

  getSearchIcon(type: string): string {
    switch (type) {
      case 'name': return 'person';
      case 'mpi': return 'badge';
      case 'identifier': return 'fingerprint';
      case 'appointment_mpi': return 'event';
      case 'appointment_eventId': return 'event';
      case 'appointment_facility': return 'business';
      case 'appointment_timerange': return 'schedule';
      default: return 'search';
    }
  }

  formatTimestamp(timestamp: number): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString();
  }
}
